import { formatUnit } from "@/lib/format-data";

import { Unit } from "@/types/enums/unit";
import type { DoseComponent, MedicationDilution } from "@/types/medication";

/** "2.5 mL": diluent added to one vial, as the product label names a reconstitution. */
export function formatDilutionLabel(diluentVolume: number): string {
  return `${Number(diluentVolume.toFixed(3))} mL`;
}

export function getDoseComponentDilutionLabel(
  component: Pick<DoseComponent, "diluentVolume">,
): string | null {
  return component.diluentVolume
    ? formatDilutionLabel(component.diluentVolume)
    : null;
}

/** The dilution a new dose starts on: the flagged default, else the first. */
export function getDefaultDilution(
  dilutions: MedicationDilution[] | undefined,
): MedicationDilution | undefined {
  return dilutions?.find((dilution) => dilution.isDefault) ?? dilutions?.[0];
}

/** Units per mL, when the vial strength is known: 100 units in 2.5 mL is 40/mL. */
export function getDilutionConcentration(
  dilution: Pick<MedicationDilution, "diluentVolume" | "vialStrength">,
): number | null {
  if (!dilution.vialStrength || !dilution.diluentVolume) return null;
  return dilution.vialStrength / dilution.diluentVolume;
}

/** "4 U / 0.1 mL": what a 0.1 mL draw holds, the way injectors dose. */
export function formatDilutionStrength(
  dilution: Pick<MedicationDilution, "diluentVolume" | "vialStrength">,
  unit?: string | null,
): string | null {
  const concentration = getDilutionConcentration(dilution);
  if (!concentration) return null;
  // A dose charted in mL says nothing about the vial's own unit.
  const unitLabel =
    unit && unit !== Unit.ML
      ? unit === Unit.UNITS
        ? "U"
        : formatUnit(unit as Unit)
      : "";
  return `${Number((concentration / 10).toFixed(2))}${unitLabel ? ` ${unitLabel}` : ""} / 0.1 mL`;
}
